import { useProxyStore } from '../../store/proxy-store';
import { getCurrentHistoryTarget } from '../../utils/current-history-target';

const logTeardownWarning = ({
  message,
  error,
}: {
  message: string;
  error: unknown;
}): void => {
  console.warn(message, {
    error: error instanceof Error ? error : new Error(String(error)),
  });
};

export async function teardownBoothPortalSession(): Promise<void> {
  const electronAPI =
    typeof window !== 'undefined' ? window.electronAPI : undefined;

  if (!electronAPI) {
    useProxyStore.getState().clearRequests();

    return;
  }

  await electronAPI.browser.closeAll().catch((error: unknown) => {
    logTeardownWarning({
      message: '[BoothReset] Failed to close browser windows',
      error,
    });
  });

  const { isProxyRunning, setProxyRunning, setProxyStopping } =
    useProxyStore.getState();

  if (isProxyRunning) {
    setProxyStopping(true);

    try {
      const result = await electronAPI.proxy.stop();

      if (result.success) {
        setProxyRunning(false);
        await electronAPI.proxy
          .setApplicationContext(null, [], null, null)
          .catch((error: unknown) => {
            logTeardownWarning({
              message: '[BoothReset] Failed to clear application context',
              error,
            });
          });
      } else {
        logTeardownWarning({
          message: '[BoothReset] Proxy stop did not succeed',
          error: new Error(
            result.error ?? result.message ?? 'Proxy stop failed',
          ),
        });
      }
    } catch (error: unknown) {
      logTeardownWarning({
        message: '[BoothReset] Proxy stop threw',
        error,
      });
    } finally {
      setProxyStopping(false);
    }
  }

  await electronAPI.proxy
    .clearRequests(getCurrentHistoryTarget())
    .catch((error: unknown) => {
      logTeardownWarning({
        message: '[BoothReset] Failed to clear proxy history',
        error,
      });
    });

  useProxyStore.getState().clearRequests();
}
